"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Card } from "@components/theming";
import { useThemeContext, useWindowSize } from "@utils/hooks";
import { useStickyContinueBarContent } from "../context/stickyContinueBarContext";

// Persistent (mounted once, in the /cart layout) sticky bottom bar for the
// cart / rego form / payment steps. Individual pages register what it shows
// via useSetStickyContinueBar; this component only owns the shared chrome,
// positioning, and the enter/exit/swap animation for the "secondary" area
// above the action button (clear-cart on the cart page, order-summary
// toggle on the payment page).
export default function StickyContinueBar() {
  const { content, prevSecondaryIdRef } = useStickyContinueBarContent();
  const windowSize = useWindowSize();
  const theme = useThemeContext();

  const isMobile = !!windowSize.width && windowSize.width < 1024;

  if (!content || !isMobile) return null;

  const secondary = content.secondary ?? null;
  // Read before this render's effect updates it, so this is genuinely the
  // id from the previous registration (or null on a real first appearance).
  const previousSecondaryId = prevSecondaryIdRef.current;
  // Only the container grows/shrinks for a genuine appear/disappear. Going
  // directly between two different secondaries (no page in between that had
  // none) just crossfades the content in place - see the inner
  // AnimatePresence below.
  const isFreshEnter = previousSecondaryId === null && secondary !== null;

  return (
    <>
      {content.backdrop}
      <Card
        style={
          {
            "--base-card-border-radius": 0,
            "--card-border-width": 0,
            "--card-background-color":
              theme && theme.appearance === "light" ? "" : "#12150340",
          } as React.CSSProperties
        }
        sharedClasses="fixed z-[49] bottom-0 left-0 w-full py-2 shadow"
        whitelabelClasses="rounded-t-[var(--radius-4)] inset-x-0"
        shopsiteClasses="bg-white rounded-t-2xl border-gray-200"
      >
        <div>
          <AnimatePresence>
            {secondary && (
              <motion.div
                key="secondary"
                initial={{ height: 0 }}
                animate={{ height: "auto", transition: { duration: 0.25 } }}
                exit={{
                  opacity: 0,
                  height: 0,
                  transition: {
                    opacity: { duration: 0.15 },
                    height: { delay: 0.15, duration: 0.2 },
                  },
                }}
                className="overflow-hidden"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={secondary.id}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: isFreshEnter
                        ? { delay: 0.2, duration: 0.2 }
                        : { duration: 0.15 },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  >
                    {secondary.content}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="px-4">{content.action}</div>
        </div>
      </Card>
    </>
  );
}
